import clsx from 'clsx';
import React from 'react';

import LineItemsListModal from './LineItemsListModal/LineItemsListModal';

import { DEFAULT_CURRENCY, DEFAULT_EMPTY_VALUE } from '../../Config';
import { TaxCalculationEnumMap } from '../../models/Treatment/TreatmentCalculator';
import { CalculationStrategy } from '../../types';
import { Treatment } from '../../types/treatment';
import LineItems from '../LineItems/LineItems';
import { CheckDisplayOption } from '../TreatmentForm/CheckPaymentForm/CheckPaymentDisplay/CheckPaymentDisplay';
import Computations from '../TreatmentForm/Computations/Computations';
import FieldWrapper from '../TreatmentForm/Fields/FieldWrapper/FieldWrapper';
import { PaidBillDisplayOption } from '../TreatmentForm/PaidBillForm/PaidBillDisplay/PaidBillDisplay';
import { UnpaidBillDisplayOption } from '../TreatmentForm/UnpaidBillForm/UnpaidBillDisplay/UnpaidBillDisplay';
import useTreatmentCalculator, { TaxCalculatorMap } from '../TreatmentForm/useTreatmentCalculator';
import TreatmentLayout from '../TreatmentLayout/TreatmentLayout';

export const FORM_TYPE_DISPLAY = {
  Bill: UnpaidBillDisplayOption,
  Expense: PaidBillDisplayOption,
  Check: CheckDisplayOption,
};

const getTaxCalculation = (
  taxCalculation: Treatment['payload']['taxCalculation']
): { strategy: CalculationStrategy } => {
  return {
    strategy: TaxCalculatorMap[TaxCalculationEnumMap[taxCalculation]],
  };
};

type TreatmentDisplayProps = {
  treatment: Treatment;
  isViewInQuickbooksEnabled: boolean;
  onBack: () => void;
};

const TreatmentDisplay = ({
  treatment,
  isViewInQuickbooksEnabled,
  onBack,
}: TreatmentDisplayProps) => {
  const { totals, lineItems } = useTreatmentCalculator({
    taxCalculation: getTaxCalculation(treatment.payload.taxCalculation),
    lineItems: treatment.payload.lineItems,
    usePriceBeforeTax: true,
  });

  const displayOption = FORM_TYPE_DISPLAY[treatment.type];
  const currency = treatment.payload?.currency || DEFAULT_CURRENCY;

  const header = (
    <p
      className={clsx(
        'tw-text-cfo-black tw-text-[18px] tw-font-brandonTextMedium',
        'tw-ml-[10px]',
        'peer-hover:tw-text-cfo-blue-700'
      )}
    >
      Expense
    </p>
  );

  const footer = isViewInQuickbooksEnabled ? (
    <React.Fragment>
      <div className={clsx('tw-flex tw-items-center tw-justify-end', 'tw-w-full')}>
        <a
          href={treatment.url}
          target="_blank"
          rel="noreferrer"
          className={clsx(
            'tw-max-w-[160px] tw-w-full',
            'tw-rounded-[5px]',
            'tw-text-center tw-font-brandonTextMedium tw-text-white',
            'tw-bg-cfo-blue-700',
            'tw-py-[5px] tw-px-[15px]',
            // (!) is not removed here as it is to override the styles from vendor.css
            'hover:tw-text-white focus:!tw-no-underline focus:!tw-text-white'
          )}
        >
          View in Quickbooks
        </a>
      </div>
    </React.Fragment>
  ) : null;

  return (
    <React.Fragment>
      <TreatmentLayout
        footer={footer}
        // balanceDue={totals.total}
        onBack={onBack}
        containerClassName={'viewMode tw-h-full tw-overflow-auto'}
        header={header}
      >
        <displayOption.Component treatment={treatment} />

        <LineItems isDisplayView={true} lineItems={lineItems} isPrefilled={false}>
          <LineItemsListModal
            lineItems={lineItems}
            containerClassName={clsx('viewMode', 'tw-flex', 'tw-p-0')}
            onClose={''}
            totals={totals}
            currency={currency}
            taxCalculation={treatment.payload.taxCalculation}
          />
        </LineItems>

        <div className={clsx('tw-flex tw-items-end tw-justify-between', 'tw-mt-[10px]')}>
          <p className={clsx('tw-mb-[25px]', 'tw-flex tw-items-center')}>
            <span className={clsx('tw-text-cfo-grey-600 tw-mr-[5px]')}>Amounts are:</span>
            <span className={clsx('tw-text-cfo-black')}>{treatment.payload.taxCalculation}</span>
          </p>
          <Computations totals={totals} readOnly={true} currency={currency} />
        </div>

        <div
          className={clsx(
            'tw-border-solid tw-border-t-[1px] tw-border-t-cfo-grey-500',
            'tw-pt-[10px] tw-mt-[10px]'
          )}
        >
          <FieldWrapper label={'Memo'} labelClassName={clsx('tw-text-cfo-black')}>
            <span className={clsx('tw-text-cfo-black')}>
              {treatment.payload.memo || DEFAULT_EMPTY_VALUE}{' '}
            </span>
          </FieldWrapper>
        </div>
      </TreatmentLayout>
    </React.Fragment>
  );
};

export default TreatmentDisplay;
